import { ColumnType, ColumnValue } from '../../../grid/types/common';
import { translateFn } from '../../../utils/translator.helper';
import { Feature } from '@nirvana/shared/constants';
import {
  EntityRole,
  ManagementStatus,
  NetworkColumn,
  NetworkColumnDetails,
  PendingActivityValues,
  SecurityIssuesValues,
} from './types';
import { OperatingSystemType } from '@nirvana/shared/dtos/types/protected-entities/common';
import { CompanyData } from '../../../company/types/company-data';

export class ColumnValidator {
  public static getValidColumns(): Array<NetworkColumnDetails> {
    const columnsDetails: Array<NetworkColumnDetails> = [
      {
        columnName: translateFn('Name'),
        column: NetworkColumn.NAME,
        key: NetworkColumn.NAME,
        type: ColumnType.STRING,
        removable: false,
        sortable: true,
      },
      {
        columnName: translateFn('Company'),
        column: NetworkColumn.COMPANY,
        key: NetworkColumn.COMPANY,
        type: ColumnType.BACKEND_SINGLE_SELECT,
        editingDisabled: true,
        removable: true,
        sortable: true,
      },
      {
        columnName: translateFn('IP'),
        column: NetworkColumn.IP,
        key: NetworkColumn.IP,
        type: ColumnType.STRING,
        removable: true,
        sortable: true,
        canCopyContent: true,
      },
      {
        columnName: translateFn('Entity type'),
        column: NetworkColumn.TYPE,
        key: NetworkColumn.TYPE,
        type: ColumnType.ENUM,
        removable: true,
        filterResetValue: [],
        values: this.getValidEntityTypeFilterValues(),
      },
      {
        columnName: translateFn('Management status'),
        column: NetworkColumn.MANAGEMENT_STATUS,
        key: NetworkColumn.MANAGEMENT_STATUS,
        type: ColumnType.ENUM,
        removable: true,
        filterResetValue: [],
        values: [
          {
            label: translateFn('Managed'),
            value: ManagementStatus.MANAGED_NETWORK_V2,
          },
          {
            label: translateFn('Unmanaged'),
            value: ManagementStatus.UNMANAGED_NETWORK_V2,
          },
        ],
      },
      {
        columnName: translateFn('Security issues'),
        column: NetworkColumn.SECURITY_ISSUES,
        key: NetworkColumn.SECURITY_ISSUES,
        type: ColumnType.ENUM,
        removable: true,
        values: this.getSecurityIssuesFilterValues(),
        filterResetValue: [],
      },
      {
        columnName: translateFn('Last seen'),
        column: NetworkColumn.LAST_SEEN,
        key: NetworkColumn.LAST_SEEN,
        type: ColumnType.SELECT,
        values: [
          { label: translateFn('Online'), value: Constants.Network.lastSyncOptions.ONLINE },
          { label: translateFn('Offline'), value: Constants.Network.lastSyncOptions.OFFLINE },
          { label: translateFn('Last 24 hours'), value: Constants.Network.lastSyncOptions.LAST_24_HOURS },
          { label: translateFn('Last 7 days'), value: Constants.Network.lastSyncOptions.LAST_7_DAYS },
          { label: translateFn('Last 30 days'), value: Constants.Network.lastSyncOptions.LAST_30_DAYS },
        ],
        removable: true,
        filterResetValue: null,
        isFilter: true,
        sortable: true,
      },
      {
        columnName: translateFn('Role'),
        column: NetworkColumn.ROLE,
        key: NetworkColumn.ROLE,
        type: ColumnType.ENUM,
        values: this.getRoleFilterValues(),
        filterResetValue: [],
        removable: true,
      },
      {
        columnName: translateFn('Endpoint type'),
        column: NetworkColumn.ENDPOINT_TYPE,
        key: NetworkColumn.ENDPOINT_TYPE,
        type: ColumnType.ENUM,
        values: [
          { label: translateFn('Server'), value: 1 },
          { label: translateFn('Workstation'), value: 0 },
        ],
        removable: true,
        filterResetValue: [],
      },
      {
        columnName: translateFn('OS type'),
        column: NetworkColumn.OS_TYPE,
        key: NetworkColumn.OS_TYPE,
        type: ColumnType.ENUM,
        values: this.getValidOsTypeFilterValues(),
        removable: true,
        filterResetValue: [],
      },
      {
        columnName: translateFn('OS version'),
        column: NetworkColumn.OS_VERSION,
        key: NetworkColumn.OS_VERSION,
        type: ColumnType.STRING,
        removable: true,
        sortable: true,
      },
      {
        columnName: translateFn('Available policies'),
        column: NetworkColumn.AVAILABLE_POLICIES,
        key: NetworkColumn.AVAILABLE_POLICIES,
        type: ColumnType.BACKEND_SINGLE_SELECT,
        removable: true,
        isHiddenInGridSettings: true,
      },
      {
        columnName: translateFn('Active policy'),
        column: NetworkColumn.POLICY,
        key: NetworkColumn.POLICY,
        type: ColumnType.BACKEND_SINGLE_SELECT,
        removable: true,
        sortable: true,
      },
      {
        columnName: translateFn('Policy status'),
        column: NetworkColumn.POLICY_STATUS,
        key: NetworkColumn.POLICY_STATUS,
        type: ColumnType.ENUM,
        removable: true,
        filterResetValue: [],
        values: [
          { label: translateFn('Active'), value: 'policyActive' },
          { label: translateFn('Applied'), value: 'policyAssigned' },
          { label: translateFn('Pending'), value: 'policyPending' },
        ],
      },
      {
        columnName: translateFn('Policy type'),
        column: NetworkColumn.POLICY_TYPE,
        key: NetworkColumn.POLICY_TYPE,
        type: ColumnType.ENUM,
        removable: true,
        filterResetValue: [],
        values: [
          { label: translateFn('Direct'), value: 'policyDirect' },
          { label: translateFn('Inherited'), value: 'policyInherited' },
          { label: translateFn('Edited by Power User'), value: 'policyPowerUser' },
        ],
      },
      {
        columnName: translateFn('Pending activity'),
        column: NetworkColumn.PENDING_ACTIVITY,
        key: NetworkColumn.PENDING_ACTIVITY,
        type: ColumnType.ENUM,
        removable: true,
        values: this.getValidPendingActivityFilterValues(),
        filterResetValue: [],
      },
      {
        columnName: translateFn('Tags'),
        column: NetworkColumn.TAGS,
        key: NetworkColumn.TAGS,
        type: ColumnType.TAGS,
        removable: true,
      },
      {
        columnName: translateFn('Label'),
        column: NetworkColumn.LABEL,
        key: NetworkColumn.LABEL,
        type: ColumnType.STRING,
        removable: true,
        sortable: true,
      },
      {
        columnName: translateFn('FQDN'),
        column: NetworkColumn.FQDN,
        key: NetworkColumn.FQDN,
        type: ColumnType.STRING,
        removable: true,
        sortable: true,
        canCopyContent: true,
      },
      {
        columnName: translateFn('Logged-in users'),
        column: NetworkColumn.USERS,
        key: NetworkColumn.USERS,
        type: ColumnType.STRING,
        removable: true,
      },
      {
        columnName: translateFn('Container ID'),
        column: NetworkColumn.CONTAINER_ID,
        key: NetworkColumn.CONTAINER_ID,
        type: ColumnType.STRING,
        removable: true,
        sortable: true,
      },
      {
        columnName: translateFn('Container image name'),
        column: NetworkColumn.CONTAINER_NAME,
        key: NetworkColumn.CONTAINER_NAME,
        type: ColumnType.STRING,
        removable: true,
        sortable: true,
      },
      {
        columnName: translateFn('Power'),
        column: NetworkColumn.POWER,
        key: NetworkColumn.POWER,
        type: ColumnType.ENUM,
        removable: true,
        values: this.getPowerFilterValues(),
        filterResetValue: [],
      },
      {
        columnName: translateFn('AWS Integration tag'),
        column: NetworkColumn.AWS_INTEGRATION_TAGS,
        key: NetworkColumn.AWS_INTEGRATION_TAGS,
        type: ColumnType.STRING,
        removable: true,
      },
      {
        columnName: translateFn('Migrated'),
        column: NetworkColumn.MIGRATED_TO_ANOTHER_COMPANY,
        key: NetworkColumn.MIGRATED_TO_ANOTHER_COMPANY,
        type: ColumnType.ENUM,
        removable: true,
        values: [
          { label: translateFn('Yes'), value: Constants.Network.migratedToAnotherCompanyOptions.YES },
          { label: translateFn('No'), value: Constants.Network.migratedToAnotherCompanyOptions.NO },
        ],
        filterResetValue: [],
      },
      {
        columnName: translateFn('Deleted'),
        column: NetworkColumn.IN_DELETED_FOLDER,
        key: NetworkColumn.IN_DELETED_FOLDER,
        type: ColumnType.ENUM,
        removable: true,
        values: [
          { label: translateFn('Yes'), value: Constants.Network.inDeletedFolderOptions.YES },
          { label: translateFn('No'), value: Constants.Network.inDeletedFolderOptions.NO },
        ],
        filterResetValue: [],
      },
    ];

    return this.validateColumns(columnsDetails);
  }

  private static validateColumns(columns: Array<NetworkColumnDetails>): Array<NetworkColumnDetails> {
    return columns.filter((column: NetworkColumnDetails) => {
      switch (column.key) {
        case NetworkColumn.ENDPOINT_TYPE:
        case NetworkColumn.FQDN:
        case NetworkColumn.IP:
        case NetworkColumn.LABEL:
        case NetworkColumn.LAST_SEEN:
        case NetworkColumn.NAME:
        case NetworkColumn.OS_TYPE:
        case NetworkColumn.OS_VERSION:
        case NetworkColumn.USERS:
        case NetworkColumn.TYPE:
        case NetworkColumn.MANAGEMENT_STATUS:
        case NetworkColumn.SECURITY_ISSUES:
        case NetworkColumn.PENDING_ACTIVITY:
        case NetworkColumn.ROLE:
        case NetworkColumn.IN_DELETED_FOLDER:
          return true;
        case NetworkColumn.POLICY_STATUS:
        case NetworkColumn.POLICY_TYPE:
        case NetworkColumn.POLICY:
        case NetworkColumn.AVAILABLE_POLICIES:
          return Application.Main.hasAnyRights([Constants.Rights.RIGHT_MANAGE_POLICIES_READ]);
        case NetworkColumn.COMPANY:
          return Application.Main.product.isCloud() && Application.Main.company.isPartner();
        case NetworkColumn.TAGS:
          return Application.Main.hasFeatures(Feature.PRODUCT_FEATURE_TAGS_VISIBLE);
        case NetworkColumn.CONTAINER_NAME:
        case NetworkColumn.CONTAINER_ID:
          return Application.Main.hasFeatures(Feature.PRODUCT_FEATURE_CONTAINER_PROTECTION_VISIBLE);
        case NetworkColumn.POWER:
        case NetworkColumn.AWS_INTEGRATION_TAGS:
          return Application.Main.hasFeatures([
            Constants.Features.PRODUCT_FEATURE_AMAZON_EC2_CLOUD_INTEGRATION_CONFIGURED,
          ]);
        case NetworkColumn.MIGRATED_TO_ANOTHER_COMPANY:
          return (
            Application.Main.company.isMspPartner() ||
            Application.Main.company.isMspCustomer() ||
            CompanyData.hasCloudSecurityForMsp(Application.Main.user.companyInfo) ||
            CompanyData.hasCloudSecurityForMspTrial(Application.Main.user.companyInfo)
          );
        default:
          throw Error(`Column not validated: ${column.key}`);
      }
    });
  }

  private static getValidEntityTypeFilterValues(): Array<ColumnValue> {
    let partnerColumns: Array<ColumnValue> = [
      {
        label: translateFn('Partner-managed company'),
        value: Constants.Network.Filters.Types.COMPANIES_PARTNER_MANAGED,
      },
      {
        label: translateFn('Self-managed company'),
        value: Constants.Network.Filters.Types.COMPANIES_SELF_MANAGED,
      },
      {
        label: translateFn('Company folder'),
        value: Constants.Network.Filters.Types.COMPANY_FOLDERS,
      },
      {
        label: translateFn('Network folder'),
        value: Constants.Network.Filters.Types.FOLDERS,
      },
      {
        label: translateFn('Physical machine'),
        value: Constants.Network.Filters.Types.COMPUTERS,
      },
      {
        label: translateFn('Virtual machine'),
        value: Constants.Network.Filters.Types.VIRTUAL_MACHINES,
      },
      {
        label: translateFn('Container'),
        value: Constants.Network.Filters.Types.CONTAINERS,
      },
      {
        label: translateFn('Golden image'),
        value: Constants.Network.Filters.Types.GOLDEN_IMAGES,
      },
      {
        label: translateFn('EC2 Instance'),
        value: Constants.Network.Filters.Types.EC2_INSTANCES,
      },
    ];

    if (!Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_CONTAINER_PROTECTION_VISIBLE])) {
      partnerColumns = partnerColumns.filter(
        (columnValue: ColumnValue) => columnValue.value !== Constants.Network.Filters.Types.CONTAINERS
      );
    }

    if (!Application.Main.hasFeatures([Constants.Features.PRODUCT_FEATURE_AMAZON_EC2_CLOUD_INTEGRATION_CONFIGURED])) {
      partnerColumns = partnerColumns.filter(column => column.value !== Constants.Network.Filters.Types.EC2_INSTANCES);
    }

    if (Application.Main.company.isPartner()) {
      return partnerColumns;
    }

    return partnerColumns.filter(
      (columnValue: ColumnValue) =>
        columnValue.value !== Constants.Network.Filters.Types.COMPANY_FOLDERS &&
        columnValue.value !== Constants.Network.Filters.Types.COMPANIES_SELF_MANAGED &&
        columnValue.value !== Constants.Network.Filters.Types.COMPANIES_PARTNER_MANAGED
    );
  }

  private static getValidPendingActivityFilterValues() {
    const columnValues: Array<ColumnValue> = [
      { label: translateFn('Pending restart'), value: PendingActivityValues.PENDING_RESTART },
      { label: translateFn('Patch pending restart'), value: PendingActivityValues.PATCH_PENDING_RESTART },
      {
        label: translateFn('Troubleshooting in progress'),
        value: PendingActivityValues.TROUBLESHOOTING_IN_PROGRESS,
      },
    ];

    if (!Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_PATCH_MANAGEMENT_VISIBLE])) {
      return columnValues.filter(
        (columnValue: ColumnValue) => columnValue.value !== PendingActivityValues.PATCH_PENDING_RESTART
      );
    }

    return columnValues;
  }

  private static getSecurityIssuesFilterValues() {
    const columnValues: Array<ColumnValue> = [
      { label: translateFn('With issues'), value: SecurityIssuesValues.WITH_SECURITY_ISSUES },
      { label: translateFn('Without issues'), value: SecurityIssuesValues.WITHOUT_SECURITY_ISSUES },
      { label: translateFn('Isolated'), value: SecurityIssuesValues.ISOLATED },
    ];

    if (!Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_ISOLATION_VISIBLE])) {
      return columnValues.filter((columnValue: ColumnValue) => columnValue.value !== SecurityIssuesValues.ISOLATED);
    }

    return columnValues;
  }

  private static getValidOsTypeFilterValues(): Array<ColumnValue> {
    const columnValues: Array<ColumnValue> = [
      { label: 'Windows', value: OperatingSystemType.WINDOWS },
      { label: 'Linux', value: OperatingSystemType.LINUX },
      { label: 'macOS', value: OperatingSystemType.MACOSX },
      { label: 'Container', value: OperatingSystemType.CONTAINER },
    ];

    if (!Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_CONTAINER_PROTECTION_VISIBLE])) {
      return columnValues.filter((columnValue: ColumnValue) => columnValue.value !== OperatingSystemType.CONTAINER);
    }

    return columnValues;
  }

  private static getRoleFilterValues(): Array<ColumnValue> {
    let allValues = [
      {
        label: translateFn('Endpoint'),
        value: EntityRole.ENDPOINT,
      },
      {
        label: translateFn('Exchange Protection'),
        value: EntityRole.EXCHANGE_PROTECTION,
      },
      {
        label: translateFn('Relay'),
        value: EntityRole.RELAY,
      },
      {
        label: translateFn('Security Server'),
        value: EntityRole.SECURITY_SERVER,
      },
      {
        label: translateFn('Container host'),
        value: EntityRole.CONTAINER_HOST,
      },
      {
        label: translateFn('Patch Caching Server'),
        value: EntityRole.PATCH_CACHING_SERVER,
      },
      {
        label: translateFn('AD Integrator'),
        value: EntityRole.AD_INTEGRATOR,
      },
      {
        label: translateFn('AD Integration'),
        value: EntityRole.AD_INTEGRATION,
      },
    ];

    //filter out exchange protection
    if (
      !Application.Main.hasFeatures([
        Feature.PRODUCT_FEATURE_EXCHANGE_INSTALL,
        Feature.PRODUCT_FEATURE_EXCHANGE,
        Feature.PRODUCT_FEATURE_EXCHANGE_INSTALL_DELEGATE,
      ])
    ) {
      allValues = allValues.filter((value: ColumnValue) => value.value !== EntityRole.EXCHANGE_PROTECTION);
    }

    // filter out container host
    if (!Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_CONTAINER_PROTECTION_VISIBLE])) {
      allValues = allValues.filter((value: ColumnValue) => value.value !== EntityRole.CONTAINER_HOST);
    }

    // filter out patch caching server
    if (!Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_PATCH_MANAGEMENT_VISIBLE])) {
      allValues = allValues.filter((value: ColumnValue) => value.value !== EntityRole.PATCH_CACHING_SERVER);
    }

    // filter out security server
    if (
      !Application.Main.hasFeatures([
        Feature.PRODUCT_FEATURE_REMOTE_ENGINES_SCANNING,
        Feature.PRODUCT_FEATURE_REMOTE_ENGINES_SCANNING_INSTALL,
        Feature.PRODUCT_FEATURE_REMOTE_ENGINES_SCANNING_INSTALL_DELEGATE,
        Feature.PRODUCT_FEATURE_REMOTE_ENGINES_SHARING,
      ])
    ) {
      allValues = allValues.filter((value: ColumnValue) => value.value !== EntityRole.SECURITY_SERVER);
    }

    if (
      !Application.Main.product.isCloud() ||
      !Application.Main.hasFeatures(Constants.Features.PRODUCT_FEATURE_AD_CLOUD_CONFIGURED)
    ) {
      allValues = allValues.filter(
        (value: ColumnValue) => value.value !== EntityRole.AD_INTEGRATION && value.value !== EntityRole.AD_INTEGRATOR
      );
    }

    return allValues;
  }

  private static getPowerFilterValues(): Array<ColumnValue> {
    return [
      {
        label: translateFn('Running'),
        value: Constants.ProtectedEntities.AWSPowerState.POWER_STATE_RUNNING,
      },
      {
        label: translateFn('Stopped'),
        value: Constants.ProtectedEntities.AWSPowerState.POWER_STATE_STOPPED,
      },
      {
        label: translateFn('Terminated'),
        value: Constants.ProtectedEntities.AWSPowerState.POWER_STATE_TERMINATED,
      },
    ];
  }
}
